import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";

export default function TestimonialCarousel({ testimonials }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    if (paused || testimonials.length < 2) return;
    const t = setInterval(() => go(1), 5000);
    return () => clearInterval(t);
  }, [paused, go, testimonials.length]);

  if (!testimonials.length) return null;
  const t = testimonials[index];

  return (
    <section className="py-20 lg:py-28 bg-[#0B132B]" data-testid="testimonials-section"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="font-mono2 text-xs uppercase tracking-widest text-[#F97316] mb-3">Client Feedback</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-none">
            Trusted on the Road
          </h2>
        </div>
        <div className="relative max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div key={t.id} data-testid={`testimonial-card-${t.id}`}
              initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="bg-[#131C38] border border-[#1E293B] rounded-lg p-8 sm:p-12 text-center relative">
              <Quote className="w-10 h-10 text-[#F97316]/30 absolute top-8 left-8" />
              <div className="flex gap-1 justify-center mb-6">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < t.rating ? "text-[#F97316] fill-[#F97316]" : "text-slate-600"}`} />
                ))}
              </div>
              <p className="text-slate-200 text-base sm:text-lg leading-relaxed mb-8">"{t.text}"</p>
              <div className="text-sm font-semibold text-white">{t.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{t.role}</div>
            </motion.div>
          </AnimatePresence>
          <div className="flex items-center justify-center gap-4 mt-8">
            <button onClick={() => go(-1)} data-testid="testimonial-prev-btn" aria-label="Previous testimonial"
              className="w-10 h-10 rounded-full border border-[#1E293B] flex items-center justify-center text-slate-400 hover:border-[#F97316] hover:text-[#F97316] transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setIndex(i)} data-testid={`testimonial-dot-${i}`} aria-label={`Testimonial ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? "w-10 bg-[#F97316]" : "w-4 bg-slate-700 hover:bg-slate-600"}`} />
              ))}
            </div>
            <button onClick={() => go(1)} data-testid="testimonial-next-btn" aria-label="Next testimonial"
              className="w-10 h-10 rounded-full border border-[#1E293B] flex items-center justify-center text-slate-400 hover:border-[#F97316] hover:text-[#F97316] transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
